import { useEffect, useState, useRef } from 'react'
import api from 'config/axiosConfig'
import { useParams } from 'react-router-dom'
import FeedbackCardRefactored from '../FeedbackCardRefactored'
import { EntityCardRef } from 'components/Entities/EntityCardBase'
import FeedbackProfileTabs from './FeedbackProfileTabs'
import { Feedback } from 'util/constants'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { apiRoutes } from 'util/apiRoutes'
import { applyPendingMediaOrder, syncPostCoauthors } from 'components/Posts/display/postProfileHelpers'
import { usePostProfileController } from 'components/Posts/display/usePostProfileController'
import {
  normalizeExistingCoauthors,
  usePostCoauthorState,
} from 'components/Posts/display/usePostCoauthorState'

const FeedbackProfile = () => {
    const { id } = useParams<{ id: string }>()
    const [title, setTitle] = useState<string>("")
    const [description, setDescription] = useState<string>("")
    const [category, setCategory] = useState<Feedback['properties']['category']>("")
    const [privacy, setPrivacy] = useState<string>("public")
    const [status, setStatus] = useState<Feedback['status']>("open")
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const {
        entity: feedback,
        setEntity: setFeedback,
        error,
        isLoading,
        editMode,
        setEditMode,
        setSearchParams,
    } = usePostProfileController<Feedback>({
        id,
        fallbackKind: "feedback",
        getDocumentTitle: (entity) => `Feedback - ${title || entity?.title || ""}`,
        notFoundError: '404: Failed to load feedback profile. Please try again later.',
        genericError: 'Failed to load feedback profile. Please try again later.',
    })

    // Validation states
    const [titleValidationMessage, setTitleValidationMessage] = useState<string>("")

    const feedbackCardRef = useRef<EntityCardRef>(null)
    const {
        coauthors,
        newCoauthors,
        removedCoauthors,
        initializeCoauthors,
        handleCoauthorsChange,
        resetCoauthorChanges,
    } = usePostCoauthorState(privacy)

    useEffect(() => {
        if (!feedback) {
            return
        }

        setTitle(feedback.title || "")
        setDescription(feedback.body || "")
        setStatus(feedback.status)
        setCategory(feedback.properties?.category || "")
        setPrivacy(feedback.properties?.privacy || "public")
        initializeCoauthors(normalizeExistingCoauthors(feedback.properties?.coauthors))
    }, [feedback, initializeCoauthors])

    const validateFields = () => {
        let isValid = true
        
        // Title validation
        if (!title.trim()) {
            setTitleValidationMessage('Title is required')
            isValid = false
        } else if (title.length > 300) {
            setTitleValidationMessage('Title must be less than 300 characters')
            isValid = false
        } else {
            setTitleValidationMessage('')
        }

        return isValid
    }

    // Update handleSave to include coauthors
    const handleSave = async () => {
        if (isSubmitting || !validateFields()) return

        setIsSubmitting(true)
        try {
            const mediaOrderSuccess = await applyPendingMediaOrder(feedbackCardRef)
            if (!mediaOrderSuccess) {
                return
            }

            await api.patch(apiRoutes.posts.byId(id || ""), {
                title,
                body: description,
                category,
                privacy,
                status
            })

            await syncPostCoauthors("feedback", id || "", newCoauthors, removedCoauthors, privacy)

            setFeedback(prev => prev ? {
              ...prev,
              title,
              body: description,
              status,
              properties: {
                ...prev.properties,
                category,
                privacy,
                coauthors: [...coauthors]
              }
            } : null)
            setEditMode(false)
            setSearchParams({})
            resetCoauthorChanges()
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        if (feedback) {
            setTitle(feedback.title || "")
            setDescription(feedback.body || "")
            setStatus(feedback.status)
            setCategory(feedback.properties?.category || "")
            setPrivacy(feedback.properties?.privacy || "public")
            initializeCoauthors(normalizeExistingCoauthors(feedback.properties?.coauthors))
        }
        setEditMode(false)
        setSearchParams({})
    }

    if (error) {
        return error.match(/404/) ? 
            <NotFoundPage 
                displayStyle="page" 
                errorMessage="Sorry, we couldn't find this feedback." 
                errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
                buttonText="View Other Feedback" 
                buttonHref="/user-feedback" 
            /> : 
            <ServerErrorPage />
    }

    if (isLoading || !feedback) {
        return <LoadingSkeleton count={1} variant="entity-card" margin="mt-4" />
    }

    const editorButtons = editMode ? (
        <div className="flex justify-end gap-2">
            <button
                onClick={handleCancel}
                className="btn-secondary py-1.5"
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                onClick={handleSave}
                className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
                disabled={isSubmitting || !title}
            >
                Save
            </button>
        </div>
    ) : null;

    return (
        <div>
            <div>
                {feedback && (
                    <FeedbackCardRefactored
                        ref={feedbackCardRef}
                        feedback={{
                            ...feedback,
                            title,
                            body: description,
                            status,
                            properties: {
                                ...feedback.properties,
                                category,
                                privacy,
                            }
                        }}
                        showMeta={true}
                        editMode={editMode}
                        isEntityProfilePage={true}
                        onTitleChange={setTitle}
                        onDescriptionChange={setDescription}
                        onCategoryChange={setCategory}
                        onPrivacyChange={setPrivacy}
                        onStatusChange={setStatus}
                        onCoauthorsChange={handleCoauthorsChange}
                        titleValidationMessage={titleValidationMessage}
                    >
                        {editorButtons}
                    </FeedbackCardRefactored>
                )}
            </div>
            <FeedbackProfileTabs feedback={feedback} />
        </div>
    )
}

export default FeedbackProfile
